/* eslint-disable indent */
import {
  TransactionDataType,
  TransactionDetailsType,
} from "../types/genericType";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { formatDate } from "./dateFormat";

// Extend dayjs with UTC and timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export const toggleDrawer =
  <TEvent extends React.KeyboardEvent | React.MouseEvent>(
    flag: boolean,
    setOpen: Function
  ) =>
  (event: TEvent) => {
    if (
      event.type === "keydown" &&
      ((event as React.KeyboardEvent).key === "Tab" ||
        (event as React.KeyboardEvent).key === "Shift")
    ) {
      return;
    }

    setOpen(flag);
  };

export const getRoleIdByRole = (role: string): number => {
  let roleId = -1;

  switch (role) {
    case "Admin":
      roleId = 4;
      break;
    case "Client":
      roleId = 2;
      break;
    case "Auditor":
      roleId = 3;
      break;
    default:
      break;
  }

  return roleId;
};

export const generateClientName = (client: any): string => {
  if (
    client.companyName &&
    client.companyName !== "null" &&
    client.companyName !== ""
  ) {
    return client.companyName;
  } else {
    if (client.firstName && client.lastName) {
      return `${client.firstName} ${client.lastName}`;
    } else if (client.firstName) {
      return client.firstName;
    } else if (client.lastName) {
      return client.lastName;
    }
  }
  return "";
};

export const getTransactionsFromData = (
  transactionData: TransactionDataType[]
) => {
  const transactions: TransactionDetailsType[] = [];
  transactionData.forEach((d) => {
    let currentAccBal = d.balance;

    const sortedTransactions = d.transactions.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      const createdOnA = new Date(a.createdOn).getTime();
      const createdOnB = new Date(b.createdOn).getTime();

      if (dateA !== dateB) return dateA - dateB;
      if (createdOnA !== createdOnB) return createdOnA - createdOnB;
      return a.id - b.id;
    });
    
    sortedTransactions.forEach((t, i) => {
      currentAccBal += d.transactions[i].amount;

      transactions.push({
        client: generateClientName(d.investor),
        clientId: d.investor.id,
        account: parseInt(d.accountNumber),
        accountName: d.name,
        fromClientId: d?.investor?.id?.toString(),
        fromAccount: d?.id,
        balance: currentAccBal,
        ...t,
        bankTransactionType: t.bankTransactionType ?? undefined,
      });
    });
  });
  return transactions.sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    const createdOnA = new Date(a.createdOn).getTime();
    const createdOnB = new Date(b.createdOn).getTime();
  
    if (dateA !== dateB) return dateB - dateA;
    if (createdOnA !== createdOnB) return createdOnB - createdOnA;
    return b.id - a.id;
  });
  
};

export const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export const getDaysInMonth = (month: number, year: number) => {
  if (month < 1 || month > 12 || isNaN(month) || isNaN(year)) {
    throw new Error("Invalid month or year input");
  }

  const lastDayOfMonth = new Date(year, month, 0).getDate();

  return lastDayOfMonth;
};

const currentDate = new Date();

export const calculateDates = (timeframe: string) => {
  switch (timeframe) {
    case "0":
      const thisMonthStartDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
      );
      return [formatDate(thisMonthStartDate), formatDate(currentDate)];
    case "1":
      const lastMonthStartDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - 1,
        1
      );
      const lastMonthEndDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        0
      );
      return [formatDate(lastMonthStartDate), formatDate(lastMonthEndDate)];
    case "2":
      break;
    default:
      break;
  }
};

export const taxandSSNFormatter = (type: string | undefined,numericValue:string | undefined) => {
  if (type === "Individual") {
    return numericValue?.replace(
      /(\d{3})(\d{2})(\d{4})/,
      "$1-$2-$3"
    );
  } else {
    // Format the string as 12-2356789
    return numericValue?.replace(/(\d{2})(\d{7})/, "$1-$2");
  }
  
};
export const formatDollar = (amount: number | string): string => {
  // Ensure the input is a number
  if (typeof amount === 'string') {
      amount = parseFloat(amount);
  }

  // Use Intl.NumberFormat to format the number as a dollar amount
  const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
  });

  return formatter.format(amount);
};
